import type { LetterResult } from '@creanizer/types'
import { Send, MapPin } from 'lucide-react'

interface LetterViewerProps {
  document: LetterResult
}

export function LetterViewer({ document }: LetterViewerProps) {
  // データ検証
  if (!document || !document.body) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">手紙</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-[#fdfcf8] shadow-xl rounded-lg overflow-hidden" 
           style={{
             backgroundImage: `
               linear-gradient(90deg, transparent 0%, transparent 49%, #e5e5e5 50%, transparent 51%, transparent 100%),
               linear-gradient(#f5f5f0 0.1em, transparent 0.1em)
             `,
             backgroundSize: '100% 100%, 100% 2em'
           }}>
        
        {/* 封筒風のヘッダー */}
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-6 border-b-2 border-amber-200">
          <div className="flex justify-between items-start">
            {/* 差出人 */}
            <div className="space-y-1">
              <p className="text-xs text-gray-600 flex items-center gap-1">
                <Send className="w-3 h-3" />
                差出人
              </p>
              <p className="font-medium text-gray-900">{document.sender?.name || '差出人'}</p>
              {document.sender?.address && (
                <p className="text-sm text-gray-600 flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5" />
                  {document.sender?.address}
                </p>
              )}
            </div>
            
            {/* 切手風の装飾 */}
            <div className="w-16 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded shadow-md flex items-center justify-center transform rotate-3">
              <span className="text-white font-bold text-xs">LETTER</span>
            </div>
          </div>
          
          {/* 宛先 */}
          <div className="mt-6 pt-4 border-t border-amber-200">
            <p className="text-xs text-gray-600">宛先</p>
            <p className="font-medium text-gray-900 text-lg">{document.recipient?.name || '受取人'} 様</p>
            {document.recipient?.address && (
              <p className="text-sm text-gray-600 mt-1">{document.recipient.address}</p>
            )}
          </div>
        </div>
        
        {/* 手紙本文 */}
        <div className="p-8 md:p-12">
          {/* 日付 */}
          <div className="text-right mb-6">
            <p className="text-gray-700">{document.date || new Date().toLocaleDateString('ja-JP')}</p>
          </div>
          
          {/* 挨拶 */}
          <div className="mb-4">
            <p className="text-lg text-gray-900 font-serif">
              {document.salutation || '拝啓'}
            </p>
          </div>
          
          {/* 本文 */}
          <div className="space-y-4 text-gray-800 font-serif leading-relaxed">
            {document.body.split('\n').map((paragraph, index) => (
              paragraph.trim() ? (
                <p key={index} className="indent-8">
                  {paragraph}
                </p>
              ) : (
                <br key={index} />
              )
            ))}
          </div>
          
          {/* 結び */}
          <div className="mt-8 text-right">
            <p className="text-gray-900 font-serif">
              {document.closing || '敬具'}
            </p>
          </div>
          
          {/* 署名 */}
          <div className="mt-4 text-right">
            <p className="text-2xl font-signature text-gray-700">
              {document.signature || document.sender?.name || '署名'}
            </p>
          </div>
          
          {/* 追伸 */}
          {document.postscript && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 font-serif">
                追伸: {document.postscript}
              </p>
            </div>
          )}
        </div>
        
        {/* 装飾的な要素 */}
        <div className="px-8 pb-4">
          <div className="flex justify-between items-center text-xs text-gray-400">
            <span>✉</span>
            <span className="italic">With warm regards</span>
            <span>✉</span>
          </div>
        </div>
      </div>
      
      <style>{`
        .font-signature {
          font-family: 'Dancing Script', cursive;
        }
        
        .font-serif {
          font-family: 'Noto Serif JP', serif;
        }
        
        .indent-8 {
          text-indent: 2rem;
        }
      `}</style>
    </div>
  )
}